import type { ICart, ICartItem, ICheckoutData, IValidateCoupon } from "./../types/general";
import useCustomFetch from "./useCustomFetch";
import { useCartStore } from "./../stores/cart.store";
import { useCookie } from "nuxt/app";
import { CartAvailabilityStatus, CartStatus } from "../types/cart.d";

export const useCart = () => {
  const accessToken = useCookie("accessToken").value;
  const cartStore = useCartStore();

  const fetchCart = async (userCartId?: string) => {
    if (window) {
      const cartId = cartStore.getCartId ?? localStorage.getItem("cartId");
      let cartResponse: ICart | null | undefined;
      let userCartResponse: ICart | null | undefined;

      if (cartId) cartResponse = await fetchCartById(cartId);
      if (userCartId && userCartId !== cartId)
        userCartResponse = await fetchCartById(userCartId);

      if (userCartResponse) {
        if (
          cartId &&
          accessToken &&
          cartResponse &&
          cartResponse?.items.length > 0 &&
          cartResponse.status !== CartStatus.CLOSED
        ) {
          const res = await mergeUserCart(cartId);
          if (res) cartStore.setCart(res);
        } else {
          cartStore.setCart(userCartResponse);
        }
      } else {
        if (!cartId && !userCartId) cartResponse = await create();

        if (cartResponse?.status === CartStatus.CLOSED)
          cartResponse = await resetCart();

        if (cartResponse) cartStore.setCart(cartResponse);
        if (accessToken && !cartResponse?.user) {
          await mergeCart();
        }
      }
    }
  };

  const removeFromCart = async (itemId: number): Promise<void> => {
    if (!cartStore.getCartId || cartStore.cart?.status === CartStatus.CLOSED)
      await resetCart();

    const res = await removeItemFromCart(cartStore.getCartId!, itemId);

    if (res) cartStore.setCart(res);
  };

  const addToCart = async (item: any): Promise<void> => {
    if (!cartStore.getCartId || cartStore.cart?.status === CartStatus.CLOSED)
      await resetCart();

    const res = await addItemsToCart(item, cartStore.getCartId!);

    if (res) cartStore.setCart(res);
  };

  const editCart = async (itemData: any, itemId:string): Promise<void> => {
    if (!cartStore.getCartId || cartStore.cart?.status === CartStatus.CLOSED)
      await resetCart();

    const res = await editCartItems(itemData, cartStore.getCartId!, itemId);

    if (res) cartStore.setCart(res);
  };

  const checkCart = async () => {
    if (cartStore.getCartId) {
      const res = checkingCart(cartStore.getCartId!);
      return res;
    }
  };

  const checkout = async (checkoutData: ICheckoutData) => {
    const res = await checkoutCart(cartStore.getCartId!, checkoutData);
    return res.url;
  };

  const mergeCart = async () => {
    if (cartStore.getCartId) {
      const res = await mergeUserCart(cartStore.getCartId);

      if (res) cartStore.setCart(res);
    }
  };

  const resetCart = async () => {
    if (window) {
      localStorage.removeItem("cartId");
      cartStore.setCart();
      const cartResponse = await create();
      if (cartResponse) cartStore.setCart(cartResponse);
      return cartResponse;
    }
  };

  const deleteUnavailableItems = async () => {
    if (!cartStore.getCartId || cartStore.cart?.status === CartStatus.CLOSED)
      await resetCart();

    const res = await deleteUnavailableItemsFromCart(cartStore.getCartId!);

    if (res) cartStore.setCart(res);
  };

  const validatePromotionCode = async (promotionCode: string) => {
    if (!cartStore.getCartId || cartStore.cart?.status === CartStatus.CLOSED)
      await resetCart();
    const res = await checkPromotionCode(cartStore.getCartId!, promotionCode);

    return res
  }

  return {
    removeFromCart,
    addToCart,
    editCart,
    checkCart,
    fetchCart,
    checkout,
    mergeCart,
    resetCart,
    deleteUnavailableItems,
    validatePromotionCode,
  };
};

const create = async (): Promise<ICart | null | undefined> => {
  const {
    data,
    errorMessage,
    postData: newCart,
  } = useCustomFetch<ICart>("/api/carts");

  await newCart<any>({});
  return data.value?.data;
};

const addItemsToCart = async (item: any, id: string): Promise<ICart | null> => {
  const {
    data,
    errorMessage,
    postData: addCart,
  } = useCustomFetch<ICart>(`/api/carts/${id}/items`);

  const res = await addCart<any>(item);
  if (data.value && data.value.data) return data.value.data;
  return null;
};

async function editCartItems (itemData: any, cartId: string, itemId: string) {
  const {
    data,
    errorMessage,
    patchData: patchCartItem,
  } = useCustomFetch<ICart>(`/api/carts/${cartId}/items/${itemId}`);

  await patchCartItem<ICart>(itemData);
  if (data.value && data.value.data) return data.value.data;
  return null;
};

const checkingCart = async (id: string) => {
  const {
    data,
    errorMessage,
    fetchData: check,
  } = useCustomFetch<ICart>(`/api/v2/carts/${id}/updates`);

  await check();
  return data.value?.data;
};

const fetchCartById = async (id: string) => {
  const { data, errorMessage, fetchData } = useCustomFetch<ICart>(
    `/api/carts/${id}`
  );

  await fetchData();
  return data.value?.data;
};

async function removeItemFromCart(cartId: string, itemId: number) {
  const {
    data,
    errorMessage,
    deleteData: remove,
  } = useCustomFetch<ICart>(`/api/carts/${cartId}/items/${itemId}`);

  await remove();
  return data.value?.data;
}

const checkoutCart = async (cartId: string, checkoutData: ICheckoutData) => {
  const { data, errorMessage, postData } = useCustomFetch<any>(
    `/api/v2/carts/${cartId}/checkout`
  );

  await postData<any>(checkoutData);

  return data.value?.data;
};

const mergeUserCart = async (id: string) => {
  const { data, errorMessage, putData } = useCustomFetch<ICart>(
    `/api/v2/carts/${id}`
  );

  await putData({});
  return data.value?.data;
};

async function deleteUnavailableItemsFromCart(cartId: string) {
  const {
    data,
    errorMessage,
    deleteData: remove,
  } = useCustomFetch<ICart>(
    `/api/carts/${cartId}/items?filter%5Bstatus%5D=${CartAvailabilityStatus.UNAVAILABLE}`
  );

  await remove();
  return data.value?.data;
}

async function checkPromotionCode(cartId:string, promotionCode: string) {
  const {
    data,
    errorMessage,
    postData: checkPromotion,
  } = useCustomFetch<IValidateCoupon>(`/api/v2/carts/${cartId}/validate-coupon`);

  await checkPromotion<{ code: string }>({ code: promotionCode });

  return data.value
}